import { supabase } from './client';
import { compressImage } from '../../utils/imageCompression';

const BUCKET_NAME = 'inspection-photos';

/**
 * Upload photo to Supabase Storage
 * @param {File} file - Image file to upload
 * @param {Object} metadata - Metadata for organizing files
 * @param {string} metadata.userId - User ID
 * @param {string} metadata.inspectionId - Inspection ID
 * @param {string} metadata.itemId - Item ID
 * @param {boolean} shouldCompress - Whether to compress before upload (default: true)
 * @returns {Promise<Object>} - { path, url, error }
 */
export async function uploadPhoto(file, metadata, shouldCompress = true) {
  try {
    const { userId, inspectionId, itemId } = metadata;

    // Validate metadata
    if (!userId || !inspectionId || !itemId) {
      throw new Error('Missing required metadata (userId, inspectionId, itemId)');
    }

    // Compress image before upload
    const fileToUpload = shouldCompress ? await compressImage(file) : file;

    // Generate unique file name
    const timestamp = Date.now();
    const fileExt = fileToUpload.name.split('.').pop();
    const fileName = `${timestamp}_${Math.random().toString(36).substring(7)}.${fileExt}`;

    // Construct storage path: userId/inspectionId/itemId/fileName
    const path = `${userId}/${inspectionId}/${itemId}/${fileName}`;

    // Upload to Supabase Storage
    const { data, error } = await supabase.storage
      .from(BUCKET_NAME)
      .upload(path, fileToUpload, {
        cacheControl: '3600',
        upsert: false,
      });

    if (error) throw error;

    // Get signed URL (valid for 1 hour) - bucket is PRIVATE
    const { data: urlData, error: urlError } = await supabase.storage
      .from(BUCKET_NAME)
      .createSignedUrl(data.path, 3600);

    if (urlError) {
      console.warn('Could not create signed URL:', urlError);
    }

    return {
      path: data.path,
      url: urlData?.signedUrl || null,
      size: fileToUpload.size,
      error: null,
    };
  } catch (error) {
    console.error('Error uploading photo:', error);
    return {
      path: null,
      url: null,
      size: null,
      error: error.message || 'Nie udało się przesłać zdjęcia',
    };
  }
}

/**
 * Delete photo from Supabase Storage
 * @param {string} path - Storage path of the photo
 * @returns {Promise<Object>} - { success, error }
 */
export async function deletePhoto(path) {
  try {
    const { error } = await supabase.storage
      .from(BUCKET_NAME)
      .remove([path]);

    if (error) throw error;

    return { success: true, error: null };
  } catch (error) {
    console.error('Error deleting photo:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Delete multiple photos from Supabase Storage
 * @param {string[]} paths - Array of storage paths
 * @returns {Promise<Object>} - { success, error }
 */
export async function deletePhotos(paths) {
  try {
    const { error } = await supabase.storage
      .from(BUCKET_NAME)
      .remove(paths);

    if (error) throw error;

    return { success: true, error: null };
  } catch (error) {
    console.error('Error deleting photos:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Get signed URL for private photo access
 * @param {string} path - Storage path of the photo
 * @param {number} expiresIn - Expiration time in seconds (default: 3600 = 1 hour)
 * @returns {Promise<Object>} - { url, error }
 */
export async function getSignedPhotoUrl(path, expiresIn = 3600) {
  try {
    const { data, error } = await supabase.storage
      .from(BUCKET_NAME)
      .createSignedUrl(path, expiresIn);

    if (error) throw error;

    return { url: data.signedUrl, error: null };
  } catch (error) {
    console.error('Error getting signed URL:', error);
    return { url: null, error: error.message };
  }
}

/**
 * Get public URL for photo - DEPRECATED (bucket is private)
 * Use getSignedPhotoUrl() instead
 * @param {string} path - Storage path of the photo
 * @returns {string} - Public URL (will not work for private bucket)
 * @deprecated
 */
export function getPublicPhotoUrl(path) {
  console.warn('getPublicPhotoUrl is deprecated for private bucket. Use getSignedPhotoUrl() instead.');
  const { data } = supabase.storage
    .from(BUCKET_NAME)
    .getPublicUrl(path);

  return data.publicUrl;
}

/**
 * List all photos for a specific item
 * @param {string} userId - User ID
 * @param {string} inspectionId - Inspection ID
 * @param {string} itemId - Item ID
 * @returns {Promise<Object>} - { photos, error }
 */
export async function listPhotos(userId, inspectionId, itemId) {
  try {
    const path = `${userId}/${inspectionId}/${itemId}`;

    const { data, error } = await supabase.storage
      .from(BUCKET_NAME)
      .list(path);

    if (error) throw error;

    // Map to include full paths (URLs must be generated separately with signed URLs)
    const photos = data.map(file => ({
      name: file.name,
      path: `${path}/${file.name}`,
      url: null, // Use getSignedPhotoUrl() to get URL when needed
      size: file.metadata?.size,
      createdAt: file.created_at,
    }));

    return { photos, error: null };
  } catch (error) {
    console.error('Error listing photos:', error);
    return { photos: [], error: error.message };
  }
}

/**
 * Calculate total storage usage for a user
 * @param {string} userId - User ID
 * @returns {Promise<Object>} - { totalBytes, totalMB, error }
 */
export async function getUserStorageUsage(userId) {
  try {
    const { data, error } = await supabase.storage
      .from(BUCKET_NAME)
      .list(userId, {
        limit: 1000,
        offset: 0,
        sortBy: { column: 'name', order: 'asc' },
      });

    if (error) throw error;

    // Calculate total size recursively
    let totalBytes = 0;
    const calculateSize = async (path) => {
      const { data: files } = await supabase.storage
        .from(BUCKET_NAME)
        .list(path);

      for (const file of files) {
        if (file.metadata?.size) {
          totalBytes += file.metadata.size;
        }
      }
    };

    await calculateSize(userId);

    return {
      totalBytes,
      totalMB: (totalBytes / 1024 / 1024).toFixed(2),
      error: null,
    };
  } catch (error) {
    console.error('Error calculating storage usage:', error);
    return { totalBytes: 0, totalMB: 0, error: error.message };
  }
}

/**
 * Upload multiple photos in batch
 * @param {File[]} files - Array of image files
 * @param {Object} metadata - Metadata for organizing files
 * @returns {Promise<Object>} - { results, successCount, errorCount }
 */
export async function uploadPhotoBatch(files, metadata) {
  const results = [];
  let successCount = 0;
  let errorCount = 0;

  for (const file of files) {
    const result = await uploadPhoto(file, metadata);
    results.push(result);

    if (result.error) {
      errorCount++;
    } else {
      successCount++;
    }
  }

  return {
    results,
    successCount,
    errorCount,
    error: errorCount > 0 ? `${errorCount} uploads failed` : null,
  };
}

/**
 * Upload user avatar to Supabase Storage
 * @param {File} file - Image file to upload
 * @param {string} userId - User ID
 * @param {boolean} shouldCompress - Whether to compress before upload (default: true)
 * @returns {Promise<Object>} - { path, url, error }
 */
export async function uploadAvatar(file, userId, shouldCompress = true) {
  try {
    // Compress image before upload
    const fileToUpload = shouldCompress ? await compressImage(file, {
      maxSizeMB: 0.5,
      maxWidthOrHeight: 512,
    }) : file;

    // Construct storage path: userId/avatar.ext (matching RLS policy)
    const fileExt = fileToUpload.name.split('.').pop();
    const path = `${userId}/avatar.${fileExt}`;

    // Upload to avatars bucket (upsert to replace old avatar)
    const { data, error } = await supabase.storage
      .from('avatars')
      .upload(path, fileToUpload, {
        cacheControl: '3600',
        upsert: true, // Replace existing avatar
      });

    if (error) throw error;

    // Get signed URL (valid for 1 hour)
    const { data: urlData, error: urlError } = await supabase.storage
      .from('avatars')
      .createSignedUrl(data.path, 3600);

    if (urlError) {
      console.warn('Could not create signed URL:', urlError);
    }

    return {
      path: data.path,
      url: urlData?.signedUrl || null,
      error: null,
    };
  } catch (error) {
    console.error('Error uploading avatar:', error);
    return {
      path: null,
      url: null,
      error: error.message || 'Nie udało się przesłać avatara',
    };
  }
}

/**
 * Delete user avatar from Supabase Storage
 * @param {string} userId - User ID
 * @returns {Promise<Object>} - { success, error }
 */
export async function deleteAvatar(userId) {
  try {
    // Try to delete all possible extensions
    const extensions = ['jpg', 'jpeg', 'png', 'webp'];
    const paths = extensions.map(ext => `${userId}/avatar.${ext}`);

    const { error } = await supabase.storage
      .from('avatars')
      .remove(paths);

    if (error) throw error;

    return { success: true, error: null };
  } catch (error) {
    console.error('Error deleting avatar:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Get signed URL for avatar
 * First lists files to find which extension exists, then creates signed URL
 * @param {string} userId - User ID
 * @param {number} expiresIn - Expiration time in seconds (default: 3600 = 1 hour)
 * @returns {Promise<Object>} - { url, error }
 */
export async function getAvatarUrl(userId, expiresIn = 3600) {
  if (!userId) {
    return { url: null, error: 'User ID is required' };
  }

  try {
    // First, list files in user's folder to find avatar
    const { data: files, error: listError } = await supabase.storage
      .from('avatars')
      .list(userId, {
        limit: 10,
        offset: 0,
      });

    if (listError) {
      // Folder doesn't exist or no access - user has no avatar
      return { url: null, error: null };
    }

    // Find avatar file (any extension)
    const avatarFile = files?.find(file => file.name.startsWith('avatar.'));

    if (!avatarFile) {
      // No avatar found
      return { url: null, error: null };
    }

    // Create signed URL for the found file
    const path = `${userId}/${avatarFile.name}`;
    const { data, error } = await supabase.storage
      .from('avatars')
      .createSignedUrl(path, expiresIn);

    if (error) {
      return { url: null, error: error.message };
    }

    return { url: data.signedUrl, error: null };
  } catch (error) {
    // Don't log errors - missing avatar is normal
    return { url: null, error: null };
  }
}
